import React, { useState } from 'react';
import { GasBadge, GasBannerAlert, GasButton, useGasI18n, useGasNotify } from '../../../shared/kit';
import {
    getTelemetrySession,
    materializeAction,
    telemetryEvent,
} from '../api';
import { transferDraftToCart } from '../draftTransfer';
import type { Allocation, SearchAction, SearchSolution, SolutionSplitProposal } from '../types';

function statusTone(status: string): 'success' | 'warning' | 'danger' | 'muted' | 'info' {
    if (status === 'full') return 'success';
    if (status === 'partial') return 'warning';
    if (status === 'none') return 'danger';
    return 'muted';
}

function statusKey(status: string, kind?: string): string {
    if (kind === 'alternative') return 'location.search.status.alternative';
    if (status === 'full') return 'location.search.status.full';
    if (status === 'partial') return 'location.search.status.partial';
    return 'location.search.status.none';
}

function allocationByItem(allocations: Allocation[]): Map<number, Allocation> {
    const map = new Map<number, Allocation>();
    for (const a of allocations) {
        const prev = map.get(a.item_id);
        if (prev) {
            map.set(a.item_id, { ...prev, quantity: prev.quantity + a.quantity });
        } else {
            map.set(a.item_id, { ...a });
        }
    }
    return map;
}

export function SolutionCard({
    solution,
    queryId,
    cartUrl,
    rank,
}: {
    solution: SearchSolution;
    queryId: string;
    cartUrl: string;
    rank: number;
}): React.ReactElement {
    const { t } = useGasI18n();
    const notify = useGasNotify();
    const [busy, setBusy] = useState<string | null>(null);
    const [stockWarning, setStockWarning] = useState<string | null>(null);

    const byItem = allocationByItem(solution.allocations || []);
    const openActions = (solution.actions || []).filter((a) => a.type === 'open');
    const prepareAction = (solution.actions || []).find((a) => a.type === 'prepare_reservation');

    async function onOpen(action: SearchAction) {
        setBusy(`open-${action.payload.item_id}`);
        try {
            const materialized = await materializeAction('open', {
                query_id: queryId,
                item_id: action.payload.item_id,
            });
            void telemetryEvent({
                query_id: queryId,
                event_type: 'click',
                object_type: 'rental_item',
                object_id: action.payload.item_id,
                rank,
                session_token: getTelemetrySession(),
            });
            const url = String(materialized.payload.url || action.payload.url || '');
            if (url) {
                window.location.assign(url);
            } else {
                notify.error(t('location.search.error.open'));
            }
        } catch (e) {
            notify.error((e as Error).message || t('location.search.error.open'));
        } finally {
            setBusy(null);
        }
    }

    /**
     * Prépare au panier la SOUS-PÉRIODE d'une location en 2 temps : brouillon
     * dédié (dates de la proposition, ligne seule), revalidé par le serveur.
     * Le panier ne portant qu'une période, ce transfert le remplace — le
     * visiteur finalise cette réservation puis revient pour le reste.
     */
    async function onPrepareSplit(proposal: SolutionSplitProposal) {
        setBusy(`split-${proposal.item_id}`);
        setStockWarning(null);
        try {
            const materialized = await materializeAction('prepare_reservation', {
                query_id: queryId,
                reservation_draft: {
                    start_at: proposal.start_at,
                    end_at: proposal.end_at,
                    lines: [{ item_id: proposal.item_id, quantity: proposal.quantity }],
                },
            });
            if (materialized.mutates || materialized.payload.executes) {
                notify.error(t('location.search.error.mutation_blocked'));
                return;
            }
            const checks = materialized.payload.availability_check || [];
            if (checks.some((c) => !c.ok)) {
                setStockWarning(t('location.search.stock_changed', { n: '1' }));
                return;
            }
            const resultDraft = materialized.payload.reservation_draft;
            if (!resultDraft) {
                notify.error(t('location.search.error.prepare'));
                return;
            }
            // Segment DATÉ ajouté au panier existant : la ligne porte sa propre
            // période, la caisse créera une réservation par segment — en une fois.
            const transfer = transferDraftToCart({
                query_id: String(materialized.payload.query_id || queryId),
                start_at: resultDraft.start_at,
                end_at: resultDraft.end_at,
                lines: [{
                    item_id: proposal.item_id,
                    quantity: proposal.quantity,
                    name: proposal.name,
                    slug: proposal.slug,
                }],
                replace: false,
                mode: 'dated',
            });
            if (!transfer.ok) {
                notify.error(t('location.search.error.transfer', { reason: transfer.error || '' }));
                return;
            }
            void telemetryEvent({
                query_id: queryId,
                event_type: 'add_to_cart',
                object_type: 'rental_item',
                object_id: proposal.item_id,
                session_token: getTelemetrySession(),
            });
            notify.success(t('location.search.split_prepared'));
        } catch (e) {
            notify.error((e as Error).message || t('location.search.error.prepare'));
        } finally {
            setBusy(null);
        }
    }

    async function onPrepare() {
        if (!prepareAction) return;
        setBusy('prepare');
        setStockWarning(null);
        try {
            const draft = prepareAction.payload.reservation_draft as {
                start_at: string;
                end_at: string;
                lines: Array<{ item_id: number; quantity: number }>;
                locale?: string;
            } | undefined;

            const materialized = await materializeAction('prepare_reservation', {
                query_id: queryId,
                reservation_draft: draft,
            });

            if (materialized.mutates || materialized.payload.executes) {
                notify.error(t('location.search.error.mutation_blocked'));
                return;
            }

            const checks = materialized.payload.availability_check || [];
            const failed = checks.filter((c) => !c.ok);
            if (failed.length > 0) {
                setStockWarning(
                    t('location.search.stock_changed', {
                        n: String(failed.length),
                    }),
                );
            }

            const resultDraft = materialized.payload.reservation_draft;
            if (!resultDraft) {
                notify.error(t('location.search.error.prepare'));
                return;
            }

            const lines = resultDraft.lines.map((line) => {
                const alloc = byItem.get(line.item_id);
                return {
                    item_id: line.item_id,
                    quantity: line.quantity,
                    name: alloc?.name,
                    slug: alloc?.slug,
                };
            });

            const transfer = transferDraftToCart({
                query_id: String(materialized.payload.query_id || queryId),
                start_at: resultDraft.start_at,
                end_at: resultDraft.end_at,
                lines,
                replace: true,
            });

            if (!transfer.ok) {
                notify.error(t('location.search.error.transfer', { reason: transfer.error || '' }));
                return;
            }

            // Les impressions du run sont des rental_item. L’événement doit
            // conserver la même identité objet pour passer l’attribution
            // fail-closed du service de télémétrie.
            for (const line of resultDraft.lines) {
                void telemetryEvent({
                    query_id: queryId,
                    event_type: 'add_to_cart',
                    object_type: 'rental_item',
                    object_id: line.item_id,
                    session_token: getTelemetrySession(),
                });
            }

            notify.success(t('location.search.draft_ready', { n: String(transfer.item_count) }));
            // Transfert vers le tunnel — pas de POST réservation ici.
            window.location.assign(cartUrl);
        } catch (e) {
            const msg = (e as Error).message || '';
            if (msg.includes('no_public_items') || msg.includes('item_not_found')) {
                notify.error(t('location.search.error.private_or_missing'));
            } else {
                notify.error(msg || t('location.search.error.prepare'));
            }
        } finally {
            setBusy(null);
        }
    }

    return (
        <article
            className={`gas-loc-csearch__solution gas-loc-csearch__solution--${solution.status}${solution.kind === 'alternative' ? ' gas-loc-csearch__solution--alt' : ''}`}
            data-status={solution.status}
            data-kind={solution.kind || 'primary'}
        >
            <header className="gas-loc-csearch__solution-head">
                <GasBadge tone={statusTone(solution.status)}>
                    {t(statusKey(solution.status, solution.kind))}
                </GasBadge>
                {solution.kind === 'alternative' ? (
                    <span className="gas-loc-csearch__alt-label">
                        {t('location.search.alternative_hint')}
                    </span>
                ) : null}
            </header>

            {(solution.explanations || []).length > 0 && (
                <ul className="gas-loc-csearch__explanations">
                    {solution.explanations.map((ex, i) => (
                        <li key={i}>{ex}</li>
                    ))}
                </ul>
            )}

            {(solution.allocations || []).length > 0 && (
                <div className="gas-loc-csearch__allocs">
                    <h3 className="gas-loc-csearch__subhead">{t('location.search.allocations')}</h3>
                    <ul>
                        {solution.allocations.map((a, i) => (
                            <li key={`${a.item_id}-${i}`} className="gas-loc-csearch__alloc">
                                <span className="gas-loc-csearch__alloc-qty">
                                    {t('location.search.qty_x', { n: String(a.quantity) })}
                                </span>
                                <span className="gas-loc-csearch__alloc-name">{a.name}</span>
                                {a.match_reasons?.some((r) => r.includes('brand') || r.includes('fallback'))
                                    || (a.requirement_text && a.name
                                        && a.requirement_text.toLowerCase().includes('blackmagic')
                                        && !a.name.toLowerCase().includes('blackmagic')) ? (
                                    <GasBadge tone="warning">{t('location.search.substitution')}</GasBadge>
                                ) : null}
                                {typeof a.available === 'number' ? (
                                    <span className="gas-loc-csearch__alloc-avail">
                                        {t('location.search.available_n', { n: String(a.available) })}
                                    </span>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {(solution.unmet || []).length > 0 && (
                <div className="gas-loc-csearch__unmet" role="status">
                    <h3 className="gas-loc-csearch__subhead">{t('location.search.unmet_title')}</h3>
                    <ul>
                        {solution.unmet.map((u, i) => (
                            <li key={i}>
                                <strong>{u.text || u.type}</strong>
                                {u.missing_quantity != null ? (
                                    <span>
                                        {' — '}
                                        {t('location.search.missing_qty', {
                                            n: String(u.missing_quantity),
                                        })}
                                    </span>
                                ) : null}
                                {u.reason ? (
                                    <span className="gas-loc-csearch__unmet-reason">
                                        {' '}({t(`location.search.unmet_reason.${u.reason}`)})
                                    </span>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {(solution.alternatives || []).length > 0 && (
                <div className="gas-loc-csearch__alternatives" role="status">
                    <h3 className="gas-loc-csearch__subhead">
                        {t('location.search.alternatives_title')}
                    </h3>
                    <ul>
                        {(solution.alternatives || []).map((alt) => (
                            <li key={`alt-${alt.item_id}`}>
                                {alt.url ? (
                                    <a href={alt.url}>{alt.name}</a>
                                ) : (
                                    <strong>{alt.name}</strong>
                                )}
                                {alt.explanation ? (
                                    <p className="gas-loc-csearch__alt-explain">{alt.explanation}</p>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {(solution.split_proposals || []).length > 0 && (
                <div className="gas-loc-csearch__split" role="status">
                    <h3 className="gas-loc-csearch__subhead">
                        {t('location.search.split_title')}
                    </h3>
                    <p className="gas-loc-csearch__split-intro">{t('location.search.split_intro')}</p>
                    <ul>
                        {(solution.split_proposals || []).map((sp) => (
                            <li key={`split-${sp.item_id}-${sp.start_at}`}>
                                {sp.url ? (
                                    <a href={sp.url}>{sp.name}</a>
                                ) : (
                                    <strong>{sp.name}</strong>
                                )}
                                {sp.explanation ? (
                                    <p className="gas-loc-csearch__split-explain">{sp.explanation}</p>
                                ) : null}
                                <GasButton
                                    type="button"
                                    variant="outline"
                                    size="s"
                                    disabled={busy !== null}
                                    onClick={() => void onPrepareSplit(sp)}
                                >
                                    {busy === `split-${sp.item_id}`
                                        ? t('location.search.loading')
                                        : t('location.search.split_prepare')}
                                </GasButton>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {(solution.lookup_answers || []).length > 0 && (
                <div className="gas-loc-csearch__answers" role="status">
                    {(solution.lookup_answers || []).map((a, i) => (
                        <p
                            key={`ans-${i}`}
                            className={`gas-loc-csearch__answer${a.found ? ' is-yes' : ' is-no'}`}
                        >
                            {a.answer}
                        </p>
                    ))}
                </div>
            )}

            {(solution.recommendations || []).length > 0 && (
                <div className="gas-loc-csearch__recommendations" role="status">
                    <h3 className="gas-loc-csearch__subhead">
                        {solution.kind === 'lookup'
                            ? t('location.search.lookup.title')
                            : t('location.search.recommendations_title')}
                    </h3>
                    <ul>
                        {(solution.recommendations || []).map((rec, i) => (
                            <li
                                key={`rec-${rec.item_id}-${i}`}
                                className={rec.nearest_for ? 'gas-loc-csearch__rec--nearest' : undefined}
                            >
                                {rec.url ? (
                                    <a href={rec.url}>{rec.name}</a>
                                ) : (
                                    <strong>{rec.name}</strong>
                                )}
                                {/* Prochaine disponibilité réelle (mode consultation),
                                    ou « non vérifiée » quand aucune date n'a été sondée. */}
                                <span
                                    className={`gas-loc-csearch__rec-state${
                                        rec.availability === 'available_now' ? ' is-now' : ''
                                    }`}
                                >
                                    {' '}
                                    ({rec.next_available_label
                                        || t('location.search.availability_unchecked')})
                                </span>
                                {rec.nearest_for ? (
                                    <span className="gas-loc-csearch__rec-note">
                                        {' — '}
                                        {t('location.search.lookup.nearest_of', { term: rec.nearest_for })}
                                    </span>
                                ) : null}
                                {rec.note ? (
                                    <span className="gas-loc-csearch__rec-note">{' — '}{rec.note}</span>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {stockWarning ? (
                <GasBannerAlert
                    type="warning"
                    title={t('location.search.stock_changed_title')}
                    message={stockWarning}
                />
            ) : null}

            <div className="gas-loc-csearch__actions">
                {openActions.map((action) => (
                    <GasButton
                        key={`open-${action.payload.item_id}`}
                        type="button"
                        variant="outline"
                        size="s"
                        disabled={busy !== null}
                        onClick={() => void onOpen(action)}
                    >
                        {busy === `open-${action.payload.item_id}`
                            ? t('location.search.loading')
                            : (action.label || t('location.search.action.open', {
                                name: String(action.payload.name || ''),
                            }))}
                    </GasButton>
                ))}
                {prepareAction && solution.status !== 'none' ? (
                    <GasButton
                        type="button"
                        variant="primary"
                        size="s"
                        disabled={busy !== null}
                        onClick={() => void onPrepare()}
                    >
                        {busy === 'prepare'
                            ? t('location.search.loading')
                            : (prepareAction.label || t('location.search.action.prepare_reservation'))}
                    </GasButton>
                ) : null}
            </div>
            <p className="gas-loc-csearch__no-mutation-hint">
                {t('location.search.no_direct_reservation')}
            </p>
        </article>
    );
}
